import React, { useCallback, useEffect, useRef, useState } from 'react'
import Modal from '../../../components/modal/modal'
import CloseSVG from '../../../assets/svg/close.svg'
import FormInput from '../../../components/form-input'
import { formatDateForInput, formValidation } from '../../../utils/functions'
import FormTextArea from '../../../components/form-textarea'
import Button from '../../../components/button'
import { useForm } from 'react-hook-form'
import FormSwitch from '../../../components/form-switch'
import FormFileInput from '../../../components/form-file-input'
import ButtonSelect from '../../../components/button-select'
import { employees as employeeAPI } from '../../../api/employees'
import { departments as departmentAPI } from '../../../api/hr/department-management'
import { teams as teamAPI } from '../../../api/hr/team-management'
import { branches as branchAPI } from '../../../api/hr/branches'
import DOMPurify from 'dompurify'
import FormSelect from '../../../components/form-select'
import AsyncSelect from 'react-select/async'
import {
  useCreateAnnouncement,
  useUpdateAnnouncement,
} from '../../../redux/employees/hook/announcement'
import { setAlert } from '../../../redux/components/components-slice'
import { useDispatch } from 'react-redux'

const SendAnnouncement = ({ isOpen, closeModal, announcementData }) => {
  const {
    register,
    handleSubmit,
    formState: { isValid },
    setValue,
    errors,
  } = useForm({
    mode: 'onChange',
  })
  const [reaction, setReaction] = useState(true)
  const [option, setOption] = useState('EMPLOYEE')
  const [target, setTarget] = useState('ALL')
  const [targetIds, setTargetIds] = useState([])
  const [asyncSelectKey, setAsyncSelectKey] = useState(0)
  const asyncSelectRef = useRef(null)
  const recipientTypeMap = {
    1: 'EMPLOYEE',
    2: 'HEAD_OF_DEPARTMENT',
    3: 'TEAM',
    4: 'DEPARTMENT',
    5: 'TEAM_LEAD',
    6: 'BRANCH_MANAGER',
    7: 'BRANCH',
  }

  const { mutateAsync: createAnnouncement, isLoading: createLoading } =
    useCreateAnnouncement()

  const { mutateAsync: updateAnnouncement, isLoading: updateLoading } =
    useUpdateAnnouncement()

  const dispatch = useDispatch()
  const isEditing = Boolean(announcementData?.id)
  const loading = createLoading || updateLoading

  // Pre-fill form when announcementData changes
  useEffect(() => {
    if (announcementData && isOpen) {
      // Set basic form values
      setValue('title', announcementData.title || '')
      setValue('content', announcementData.content || '')
      const formatedDate = formatDateForInput(announcementData?.publish_date)
      setValue('publish_date', formatedDate)

      console.log(formatedDate, 'formatedDate')

      // Set reaction state
      setReaction(announcementData.is_reactive ?? 0)

      // Set recipient type (convert from number to string)
      const recipientTypeString =
        recipientTypeMap[announcementData.recipient_type] || 'EMPLOYEE'

      setOption(recipientTypeString)

      // Determine target (ALL or SPECIFIC)
      const hasSpecificRecipients =
        announcementData.recipients_list &&
        announcementData.recipients_list.length > 0
      setTarget(hasSpecificRecipients ? 'SPECIFIC' : 'ALL')

      // Set target IDs for SPECIFIC recipients
      if (hasSpecificRecipients) {
        const selectedIds = announcementData.recipients_list.map(
          (recipient) => ({
            label: `${recipient.first_name} ${recipient.last_name}`,
            value: recipient.id,
          })
        )
        setTargetIds(selectedIds)
      } else {
        setTargetIds([])
      }

      setAsyncSelectKey((prev) => prev + 1)
    }
  }, [announcementData, isOpen, setValue])

  // Memoized load options for employees
  const loadEmployeeOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        employeeAPI
          .getEmployees({
            limit: 10,
            statusFilter: 1,
            search: inputValue,
            page: 1,
          })
          .then((response) => {
            const data = response.data.map((item) => ({
              label: `${item.first_name} ${item.last_name}`,
              value: item.id,
            }))
            resolve(data)
          })
          .catch(() => {
            resolve([])
          })
      }),
    []
  )

  // Memoized load options for branch - with manager data
  const loadBranchOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        branchAPI
          .getBranches({
            limit: 10,
            search: inputValue,
            page: 1,
          })
          .then((response) => {
            const data = response.data.map((item) => ({
              label: item.name,
              value: item.id,
              managerId: item.manager_setting?.manager_data?.id, // Get manager ID
            }))
            resolve(data)
          })
          .catch(() => {
            resolve([])
          })
      }),
    []
  )

  // Memoized load options for departments - with manager data
  const loadDepartmentOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        departmentAPI
          .getDepartments({
            limit: 10,
            search: inputValue,
            page: 1,
          })
          .then((response) => {
            const data = response.data.data.map((item) => ({
              label: item.name,
              value: item.id,
              managerId: item.manager_setting?.manager_data?.id, // Get manager ID
            }))
            resolve(data)
          })
          .catch(() => {
            resolve([])
          })
      }),
    []
  )

  // Memoized load options for teams - with manager data
  const loadTeamOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        teamAPI
          .getTeams({
            limit: 10,
            search: inputValue,
            page: 1,
          })
          .then((response) => {
            const data = response?.data?.map((item) => ({
              label: item.name,
              value: item.id,
              managerId: item.manager_setting?.manager_data?.id, // Get manager ID
            }))
            resolve(data)
          })
          .catch((error) => {
            resolve([])
          })
      }),
    []
  )

  // Create a memoized loadOptions function that depends on the current option
  const loadOptions = useCallback(
    (inputValue) => {
      switch (option) {
        case 'EMPLOYEE':
          return loadEmployeeOptions(inputValue)
        case 'BRANCH':
        case 'BRANCH_MANAGER':
          return loadBranchOptions(inputValue)
        case 'DEPARTMENT':
        case 'HEAD_OF_DEPARTMENT':
          return loadDepartmentOptions(inputValue)
        case 'TEAM':
        case 'TEAM_LEAD':
          return loadTeamOptions(inputValue)
        default:
          return loadEmployeeOptions(inputValue)
      }
    },
    [
      option,
      loadEmployeeOptions,
      loadDepartmentOptions,
      loadTeamOptions,
      loadBranchOptions,
    ]
  )

  const handleOptionChange = (newOption) => {
    setOption(newOption)
    setTargetIds([])
    setAsyncSelectKey((prev) => prev + 1)
  }

  const getSelectLabel = () => {
    switch (option) {
      case 'EMPLOYEE':
        return 'employees'
      case 'BRANCH':
        return 'branches'
      case 'BRANCH_MANAGER':
        return 'branch managers'
      case 'DEPARTMENT':
        return 'departments'
      case 'HEAD_OF_DEPARTMENT':
        return 'department managers'
      case 'TEAM':
        return 'teams'
      case 'TEAM_LEAD':
        return 'team managers'
      default:
        return 'items'
    }
  }

  const targetOptions = [
    { label: `All ${getSelectLabel()}`, value: 'ALL' },
    { label: `Select ${getSelectLabel()}`, value: 'SPECIFIC' },
  ]

  const submitForm = async (data) => {
    const selectedDate = new Date(data.publish_date)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (selectedDate < today) {
      dispatch(setAlert(true, 'warning', 'Date can not be in the past'))
      return
    }
    const title = DOMPurify.sanitize(data.title)
    const content = DOMPurify.sanitize(data.content)
    const publish_date = DOMPurify.sanitize(data.publish_date)

    const formData = new FormData()

    formData.append('title', title)
    formData.append('content', content)
    formData.append('publish_date', publish_date)
    formData.append('recipient_type', option)
    formData.append('is_reactive', reaction ? 1 : 0)

    // Build recipient_details with array notation
    if (target === 'ALL') {
      formData.append('recipient_details[target]', 'ALL')
    } else {
      formData.append('recipient_details[target]', 'SPECIFIC')

      // For manager types, we need to extract manager IDs from the selected items
      const selectedItems = targetIds

      switch (option) {
        case 'EMPLOYEE':
          selectedItems.forEach((item, index) => {
            formData.append(
              `recipient_details[employee_ids][${index}]`,
              item.value
            )
          })
          break
        case 'BRANCH':
          selectedItems.forEach((item, index) => {
            formData.append(
              `recipient_details[branch_ids][${index}]`,
              item.value
            )
          })
          break
        case 'BRANCH_MANAGER':
          selectedItems.forEach((item, index) => {
            // For branch managers, send the manager ID instead of branch ID
            if (item.managerId) {
              formData.append(
                `recipient_details[branch_manager_ids][${index}]`,
                item.managerId
              )
            }
          })
          break
        case 'DEPARTMENT':
          selectedItems.forEach((item, index) => {
            formData.append(
              `recipient_details[department_ids][${index}]`,
              item.value
            )
          })
          break
        case 'HEAD_OF_DEPARTMENT':
          selectedItems.forEach((item, index) => {
            // For department managers, send the manager ID instead of department ID
            if (item.managerId) {
              formData.append(
                `recipient_details[department_lead_ids][${index}]`,
                item.managerId
              )
            }
          })
          break
        case 'TEAM':
          selectedItems.forEach((item, index) => {
            formData.append(`recipient_details[team_ids][${index}]`, item.value)
          })
          break
        case 'TEAM_LEAD':
          selectedItems.forEach((item, index) => {
            // For team managers, send the manager ID instead of team ID
            if (item.managerId) {
              formData.append(
                `recipient_details[team_lead_ids][${index}]`,
                item.managerId
              )
            }
          })
          break
        default:
          break
      }
    }

    const attachment = data?.file?.[0]
    if (attachment?.size > 2 * 1024 * 1024) {
      dispatch(setAlert(true, 'warning', 'File size must not exceed 10MB.'))
      return
    }
    if (attachment) {
      formData.append('attachment', attachment)
    }

    try {
      // await createAnnouncement(formData).then((response) => {
      //   closeModal()
      //   setTargetIds([])
      //   setTarget('ALL')
      //   setOption('EMPLOYEE')
      //   setAsyncSelectKey(0)
      // })
      if (isEditing) {
        // Update existing announcement - use POST method
        await updateAnnouncement({
          id: announcementData.id,
          data: formData,
        }).then((response) => {
          closeModal()
          setTargetIds([])
          setTarget('ALL')
          setOption('EMPLOYEE')
          setAsyncSelectKey(0)
        })
      } else {
        // Create new announcement
        await createAnnouncement(formData).then((response) => {
          closeModal()
          resetForm()
          closeModal()
          setTargetIds([])
          setTarget('ALL')
          setOption('EMPLOYEE')
          setAsyncSelectKey(0)
        })
      }
    } catch (error) {
      console.error('Error creating announcement:', error)
    }
  }

  // Reset when target changes
  useEffect(() => {
    setTargetIds([])
    setAsyncSelectKey((prev) => prev + 1)
  }, [target])

  // Check if form is valid for submission
  const isFormValid = () => {
    if (!isValid) return false

    // If target is SPECIFIC, must have at least one item selected
    if (target === 'SPECIFIC' && targetIds.length === 0) {
      return false
    }

    // For manager types, ensure selected items have manager IDs
    if (
      target === 'SPECIFIC' &&
      (option === 'BRANCH_MANAGER' ||
        option === 'HEAD_OF_DEPARTMENT' ||
        option === 'TEAM_LEAD')
    ) {
      const hasManagerIds = targetIds.every((item) => item.managerId)
      if (!hasManagerIds) {
        return false
      }
    }

    return true
  }

  return (
    <Modal
      scrollable={true}
      className='!min-w-[500px] max-w-[90%] !rounded-none'
      isOpen={isOpen}
      centered={true}
    >
      <form
        onSubmit={handleSubmit(submitForm)}
        className='flex flex-col w-full p-3  overflow-auto'
      >
        <div className='flex justify-between items-center'>
          <h1 className='text-[20px] font-medium'>{isEditing ? "Updat" : 'Create'} Announcement </h1>
          <div className='cursor-pointer' onClick={() => closeModal()}>
            <img src={CloseSVG} alt='close' />
          </div>
        </div>
        <hr className='divider mb-2' />
        <section className='flex flex-col gap-2'>
          <div className='w-full'>
            <FormInput
              label='Announcement Heading'
              name='title'
              placeholder={'Enter title'}
              type='text'
              error={errors.title}
              errorMessage={errors.title && errors.title.message}
              inputRef={register(formValidation('text', true))}
              defaultValue={announcementData?.title}
            />
          </div>

          <div className='w-full'>
            <FormFileInput
              multiSource={true}
              inputRef={register(formValidation('file', false))}
              accept='any'
              value={announcementData?.attachment}
            />
          </div>
          <div className='w-full'>
            <FormTextArea
              label={`Announcement Body`}
              name='content'
              type='text'
              defaultValue={announcementData?.content}
              placeholder={'Custom announcement message'}
              error={errors.content}
              errorMessage={errors.content && errors.content.message}
              inputRef={register(formValidation('text', true))}
            />
          </div>
          <div className='w-full'>
            <FormInput
              label='Scheduled Date'
              name='publish_date'
              placeholder={'Select Date'}
              defaultValue={formatDateForInput(announcementData?.publish_date)}
              type='date'
              error={errors.publish_date}
              errorMessage={errors.publish_date && errors.publish_date.message}
              inputRef={register(formValidation('date', true))}
            />
          </div>
          <section className='flex flex-col gap-2'>
            <p className='font-medium text-sm text-gray-600'>Recipients</p>
            <div className='flex items-start w-full flex-wrap gap-3 mb-2'>
              <div className='w-[120px] h-[40px]'>
                <ButtonSelect
                  title='Employees'
                  isActive={option === 'EMPLOYEE'}
                  onClick={() => handleOptionChange('EMPLOYEE')}
                  className='w-full h-full'
                  tick={false}
                />
              </div>

              <div className='w-[150px] h-[40px]'>
                <ButtonSelect
                  title='Branch'
                  isActive={option === 'BRANCH'}
                  onClick={() => handleOptionChange('BRANCH')}
                  className='w-full h-full'
                  tick={false}
                />
              </div>

              <div className='w-[240px] h-[40px]'>
                <ButtonSelect
                  title='Branch Manager'
                  isActive={option === 'BRANCH_MANAGER'}
                  onClick={() => handleOptionChange('BRANCH_MANAGER')}
                  className='w-full h-full'
                  tick={false}
                />
              </div>

              <div className='w-[150px] h-[40px]'>
                <ButtonSelect
                  title='Department'
                  isActive={option === 'DEPARTMENT'}
                  onClick={() => handleOptionChange('DEPARTMENT')}
                  className='w-full h-full'
                  tick={false}
                />
              </div>

              <div className='w-[240px] h-[40px]'>
                <ButtonSelect
                  title='Head of Department'
                  isActive={option === 'HEAD_OF_DEPARTMENT'}
                  onClick={() => handleOptionChange('HEAD_OF_DEPARTMENT')}
                  className='w-full h-full'
                  tick={false}
                />
              </div>

              <div className='w-[100px] h-[40px]'>
                <ButtonSelect
                  title='Team'
                  isActive={option === 'TEAM'}
                  onClick={() => handleOptionChange('TEAM')}
                  className='w-full h-full'
                  tick={false}
                />
              </div>
              <div className='w-[200px] h-[40px]'>
                <ButtonSelect
                  title='Head of Team'
                  isActive={option === 'TEAM_LEAD'}
                  onClick={() => handleOptionChange('TEAM_LEAD')}
                  className='w-full h-full'
                  tick={false}
                />
              </div>
            </div>
          </section>
          <div className='w-full '>
            <FormSelect
              label={`Select ${getSelectLabel()} target`}
              placeholder={`Select ${getSelectLabel()} target`}
              value={target}
              options={targetOptions}
              onChange={(selected) => {
                setTarget(selected)
              }}
            />
          </div>
          {target === 'SPECIFIC' && (
            <>
              <div className='border bg-highlight p-2 rounded-md'>
                <label className='text-[13px] leading-[23px] font-normal text-color-gray mb-1'>
                  Select {getSelectLabel()}
                </label>
                <AsyncSelect
                  key={asyncSelectKey}
                  ref={asyncSelectRef}
                  isMulti
                  cacheOptions
                  defaultOptions
                  placeholder={`Search and select...`}
                  className='text-[13px]'
                  loadOptions={loadOptions}
                  onChange={(options) => setTargetIds(options || [])}
                  value={targetIds}
                  classNamePrefix='mySelect'
                />
              </div>
              <hr className='divider mt-3 mb-0' />
            </>
          )}
          <div className='w-[260px]'>
            <FormSwitch
              label={'Allow reactions and comments'}
              name={'reactions-comment'}
              checked={reaction}
              onClick={() => setReaction(!reaction)}
              value={reaction}
            />
          </div>
          <div className='w-full h-full'>
            <Button
              disabled={!isFormValid() || loading}
              text={isEditing ? 'Update Announcement' : 'Send Announcement'}
              type='submit'
              loading={loading}
            />
          </div>
        </section>
      </form>
    </Modal>
  )
}

export default SendAnnouncement
